import React, { createContext, useContext, useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const token =
        localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const userData =
        localStorage.getItem('userData') || sessionStorage.getItem('userData');

      if (token && userData) {
        const parsedUser = JSON.parse(userData);
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 3000;

        if (tokenPayload?.exp > currentTime) {
          setUser(parsedUser);
        } else {
          localStorage.clear();
          sessionStorage.clear();
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.clear();
      sessionStorage.clear();
    } finally {
      setLoading(false);
    }
  }, []);

  const login = (userData, token) => {
    setUser(userData);
    localStorage.setItem('authToken', token);
    localStorage.setItem('userData', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.clear();
    sessionStorage.clear();
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' ,
    isSuperAdmin :user?.role === 'super_admin',
    isEmployee: user?.role === 'employee',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const NavigationGuard = ({
  children,
  requireAuth = true,
  requireRole = null,
  redirectTo = '/login',
}) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (requireRole && user?.role !== requireRole) {
    if (user?.role === 'admin') {
      return <Navigate to="/admin-dashboard" replace />;
    } else if (user?.role === 'employee') {
      return <Navigate to="/employee-dashboard" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  if (!requireAuth && isAuthenticated) {
    if (user?.role === 'admin') {
      return <Navigate to="/admin-dashboard" replace />;
    } else if (user?.role === 'employee') {
      return <Navigate to="/employee-dashboard" replace />;
    }
  }

  return children;
};

export const PublicRoute = ({ children }) => (
  <NavigationGuard requireAuth={false}>{children}</NavigationGuard>
);

export const ProtectedRoute = ({ children, requireRole = null }) => (
  <NavigationGuard requireAuth={true} requireRole={requireRole}>
    {children}
  </NavigationGuard>
);

export const AdminRoute = ({ children }) => (
  <NavigationGuard requireAuth={true} requireRole="admin">
    {children}
  </NavigationGuard>
);

export const EmployeeRoute = ({ children }) => (
  <NavigationGuard requireAuth={true} requireRole="employee">
    {children}
  </NavigationGuard>
);

export default NavigationGuard;
